import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { OPTIONAL_CONSENT_PROVIDERS } from '../../cookie-consent/cookie-consent.config';
import { CookieConsentConfigService } from '../../cookie-consent/cookie-consent-config.service';
import {
  buildCookieModalTextSegments,
  normalizeCookieModalParamText,
  sanitizeCookieModalHtml,
  type ModalTextSegment,
} from '../../cookie-consent/cookie-consent-text.util';
import { CookieConsentService } from '../../cookie-consent/cookie-consent.service';
import type {
  ConsentDecision,
  CookieConsentPreferences,
  CookieConsentProviderDefinition,
  CookieConsentProviderId,
} from '../../cookie-consent/cookie-consent.model';

declare var $: any;

type ModalView = 'main' | 'customize';

const CUSTOMIZE_PROVIDER_ORDER: CookieConsentProviderId[] = [
  'googleAnalytics',
  'clarity',
  'youtube',
];

@Component({
  selector: 'app-clarity-consent-modal',
  templateUrl: './clarity-consent-modal.component.html',
  styleUrls: ['./clarity-consent-modal.component.scss'],
  standalone: false,
})
export class ClarityConsentModalComponent implements OnInit, OnDestroy {
  open = false;
  view: ModalView = 'main';
  modalText = '';
  policyUrl = '';
  textSegments: ModalTextSegment[] = [];
  readonly customizeProviders: CookieConsentProviderDefinition[] = [...OPTIONAL_CONSENT_PROVIDERS].sort(
    (left, right) =>
      CUSTOMIZE_PROVIDER_ORDER.indexOf(left.id) - CUSTOMIZE_PROVIDER_ORDER.indexOf(right.id)
  );
  draft: CookieConsentPreferences = {
    clarity: 'rejected',
    googleAnalytics: 'rejected',
    youtube: 'rejected',
  };

  @ViewChild('personalizarBtn') personalizarBtn?: ElementRef<HTMLButtonElement>;
  @ViewChildren('customizeCheckbox') customizeCheckboxes?: QueryList<ElementRef<HTMLInputElement>>;

  private configSub: Subscription | null = null;

  constructor(
    private readonly consent: CookieConsentService,
    private readonly modalConfig: CookieConsentConfigService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.configSub = this.modalConfig.loadConfig().subscribe((cfg) => {
      this.modalText = cfg.modalText;
      this.policyUrl = cfg.policyUrl;
      this.textSegments = buildCookieModalTextSegments(cfg.modalText);
    });

    if (this.consent.shouldPromptUser()) {
      this.openMainModal();
    }
  }

  ngOnDestroy(): void {
    this.configSub?.unsubscribe();
  }

  openMainModal(): void {
    this.view = 'main';
    this.open = true;
    $('#consentModal').modal('show');
  }

  openCustomize(): void {
    this.loadDraftFromConsent();
    this.view = 'customize';
    this.focusFirstCustomizeCheckbox();
  }

  backToMain(): void {
    this.view = 'main';
    this.focusPersonalizarButton();
  }

  onCloseMain(): void {
    this.onRejectAll();
  }

  onCloseCustomize(): void {
    this.backToMain();
  }

  closeModal(): void {
    this.open = false;
    this.view = 'main';
    $('#consentModal').modal('hide');
  }

  onAcceptAll(): void {
    this.consent.acceptAll();
    this.closeModal();
  }

  onRejectAll(): void {
    this.consent.rejectAll();
    this.closeModal();
  }

  onSavePreferences(): void {
    this.consent.updatePreferences({ ...this.draft });
    this.closeModal();
  }

  onDraftCheckboxChange(
    provider: keyof CookieConsentPreferences,
    event: Event
  ): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.setDraftDecision(provider, checked ? 'accepted' : 'rejected');
  }

  setDraftDecision(
    provider: keyof CookieConsentPreferences,
    decision: ConsentDecision
  ): void {
    if (decision === 'unset') {
      return;
    }
    this.draft = { ...this.draft, [provider]: decision };
  }

  isDraftAccepted(provider: keyof CookieConsentPreferences): boolean {
    return this.draft[provider] === 'accepted';
  }

  trackSegment(index: number, segment: ModalTextSegment): string {
    return `${index}-${segment.isPolicyLink}-${segment.text.length}`;
  }

  segmentHtml(segment: ModalTextSegment): SafeHtml {
    if (segment.text === '\n\n') {
      return this.sanitizer.bypassSecurityTrustHtml('<br><br>'); // NOSONAR — separador de párrafo
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      sanitizeCookieModalHtml(normalizeCookieModalParamText(segment.text))
    ); // NOSONAR — whitelist en util
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (!this.open) {
      return;
    }
    if (this.view === 'customize') {
      this.onCloseCustomize();
    }
  }

  private loadDraftFromConsent(): void {
    const current = this.consent.getConsent();
    this.draft = {
      clarity: current?.clarity === 'accepted' ? 'accepted' : 'rejected',
      googleAnalytics:
        current?.googleAnalytics === 'accepted' ? 'accepted' : 'rejected',
      youtube: current?.youtube === 'accepted' ? 'accepted' : 'rejected',
    };
  }

  private focusPersonalizarButton(): void {
    queueMicrotask(() => this.personalizarBtn?.nativeElement.focus());
  }

  private focusFirstCustomizeCheckbox(): void {
    queueMicrotask(() => this.customizeCheckboxes?.first?.nativeElement.focus());
  }
}
